import React, { useEffect, useState, useContext, useMemo } from "react";
import "../styles/SeatPage.css";
import { IoArrowBackOutline, IoArrowForward } from "react-icons/io5";
import { MdEventSeat } from "react-icons/md";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import reserveSeats from "../components/Reservation";
import { getAllHallSchedules, appendBookedSeatsToSchedule, getBookedSeatsForSchedule, saveBooking } from "../config/firebaseService";
import { AuthContext } from "../components/AuthContext";
import { Pie } from "react-chartjs-2";

import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
Chart.register(ArcElement, Tooltip, Legend);

// Fetch available seats for a given show ID
const fetchSeats = async (showId) => {
    try {
        const response = await fetch(`/api/seats/${showId}`);
        if (!response.ok) {
            throw new Error("Error fetching the seats");
        }
        const data = await response.json();
        return data.seats || [];
    } catch (error) {
        console.error(error);
        return [];
    }
};

//to show hall occupancy directly on the seat page for admin
const fetchShowStats = async (showId, setStats) => {
    try {
        const response = await fetch(`/api/hall_occupancy?show_id=${showId}`);

        if (!response.ok) {
            console.error(`Error fetching hall occupancy: ${response.status}`);
            return;
        }

        const data = await response.json();
        console.log("Fetched show stats:", data);
        if (data.length > 0) {
            const showData = data[0];
            setStats(showData);
        }
    } catch (error) {
        console.error("Error fetching hall occupancy:", error);
    }
};

// Seat selection component
export default function SeatSelection() {
    const { showId } = useParams();
    const location = useLocation();
    const { user, resetLogoutTimer } = useContext(AuthContext);
    const isAdmin = user?.role === "Admin";
    const navigate = useNavigate();
    const [seats, setSeats] = useState([]);
    const [selectedSeats, setSelectedSeats] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [stats, setStats] = useState(null);
    const [showChart, setShowChart] = useState(false);
    // Structured times: [{ time: 'HH:MM', hall, date, showtimeId }]
    // Note: availableTimes UI not used currently; remove to avoid unused variable warning.
        const todayStr = new Date().toISOString().split('T')[0];

    // Fetch seat data when component loads, then overlay Firestore bookedSeats for this hall/date/time
    useEffect(() => {
        if (!showId) {
            navigate("/reservations");
            return;
        }
        const mv = location.state?.movieSummary || {};
        const hallId = mv?.hall || "Hall1";
        const date = mv?.date || new Date().toISOString().split('T')[0];
        const time = mv?.showtime || mv?.time || "20:00";
        const movieId = mv?.id;

        fetchSeats(showId).then(async (baseSeats) => {
            try {
                const booked = await getBookedSeatsForSchedule({ hallId, date, time, movieId });
                if (Array.isArray(booked) && booked.length) {
                    const bookedSet = new Set(booked.map(String));
                    const merged = baseSeats.map((s) => {
                        const label = `${s.row_number}-${s.seat_number}`;
                        return bookedSet.has(label) ? { ...s, isbooked: true } : s;
                    });
                    setSeats(merged);
                } else {
                    setSeats(baseSeats);
                }
            } catch (e) {
                console.warn("Failed to load bookedSeats from Firestore:", e);
                setSeats(baseSeats);
            }
        });
        fetchShowStats(showId, setStats);
    }, [showId, navigate, location.state]);

    // Calculate total price based on selected seats
    useEffect(() => {
        setTotalPrice(selectedSeats.reduce((acc, seatId) => {
            const seat = seats.find(s => s.id === seatId);
            return acc + (seat ? Number(seat.price) : 0);
        }, 0));
    }, [selectedSeats, seats]);

    // Load available showtimes for this movie and date (default to today) from Firestore
    useEffect(() => {
        const loadTimes = async () => {
            try {
                const halls = await getAllHallSchedules();
                const mv = location.state?.movieSummary || {};
                const mvId = mv?.id ? parseInt(String(mv.id)) : null;
                // const mvHall = mv?.hall ? String(mv.hall) : null; // not used
                const mvDate = mv?.date ? String(mv.date) : todayStr;

                                const entries = [];
                for (const hallDoc of halls) {
                    const hallName = hallDoc.hall || hallDoc.id;
                    const sch = Array.isArray(hallDoc.schedule) ? hallDoc.schedule : [];
                    for (const s of sch) {
                        const sId = s.movieId !== undefined ? parseInt(String(s.movieId)) : null;
                        if (mvId !== null && sId !== mvId) continue;
                        const time = (s.time || (s.showtime?.split(" ")[1] ?? null))?.trim();
                        const date = s.date || (s.showtime?.split(" ")[0] ?? null);
                        if (mvDate && date && String(date) !== String(mvDate)) continue;
                                                if (time) {
                                                    const stCombined = s.showtime || (date && time ? `${date} ${time}` : String(time));
                                                    entries.push({ time, hall: hallName, date: date ?? "", showtimeId: `${hallName}-${stCombined}` });
                                                }
                    }
                }
                                // Show all available entries (even across halls) for the given date
                                const filtered = entries;
                                // Deduplicate by time and sort naturally
                                    // Deduplicate by time and sort lexicographically (HH:mm)
                                                // Deduplicate by (hall,time) so two identical times in same hall collapse, but
                                                // different halls with same time still show as separate options.
                                                const keyed = new Map();
                                                for (const e of filtered) {
                                                    const key = `${e.hall}||${e.time}`;
                                                    if (!keyed.has(key)) keyed.set(key, e);
                                                }
                                                    // unique sorted list available if needed for UI:
                                                    // const unique = Array.from(keyed.values()).sort((a,b) => a.time.localeCompare(b.time));
                                // Optionally could set into state for a future time-picker UI
            } catch (e) {
                console.warn("Failed to load showtimes:", e);
                // no-op: UI doesn't render time list currently
            }
        };
        loadTimes();
    }, [location.state, todayStr]);

    // Handle seat selection
    const toggleSeatSelection = (seatId) => {
        setSelectedSeats((prevSelectedSeats) => {
            if (prevSelectedSeats.includes(seatId)) {
                return prevSelectedSeats.filter(id => id !== seatId);
            } else {
                return [...prevSelectedSeats, seatId];
            }
        });
    };

    //admin can delete seats and reservations
    const deleteReservation = async (seatId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this reservation?");
    if (!confirmDelete) return;

    try {
        const response = await fetch(`/api/delete_reservation?seat_id=${seatId}`, {
            method: "DELETE",
        });

        const data = await response.json();
        if (data.success) {
            alert("Reservation deleted successfully!");

            setSeats(prevSeats =>
                prevSeats.map(seat =>
                    seat.id === seatId ? { ...seat, isbooked: false } : seat
                )
            );
            fetchShowStats(showId, setStats);
        } else {
            alert(`Error: ${data.message}`);
        }
    } catch (error) {
        console.error("Error deleting reservation:", error);
        alert("Server error while deleting reservation.");
    }
};

    // Determine if the selected showtime has already ended
    const showEnded = useMemo(() => {
        const mv = location.state?.movieSummary || {};
        // Extract date and time from state (supports combined showtime "YYYY-MM-DD HH:MM")
        let dateStr = mv?.date ? String(mv.date) : null;
        let timeStr = null;
        if (mv?.showtime) {
            const s = String(mv.showtime).trim();
            if (s.includes(" ")) {
                const [d, t] = s.split(" ");
                dateStr = dateStr || d;
                timeStr = t;
            } else {
                timeStr = s;
            }
        }
        if (!timeStr && mv?.time) timeStr = String(mv.time).trim();

        // Fallback to today's local date if none provided
        if (!dateStr) {
            const now = new Date();
            const pad = (n) => String(n).padStart(2, "0");
            dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        }

        // Normalize HH:mm
        if (timeStr) {
            const m = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
            if (m) {
                const hh = m[1].padStart(2, "0");
                const mm = m[2];
                timeStr = `${hh}:${mm}`;
            }
        }

        if (!timeStr) return false; // if we can't determine, don't block

        const dt = new Date(`${dateStr}T${timeStr}:00`);
        if (Number.isNaN(dt.getTime())) return false;
        return dt.getTime() < Date.now();
    }, [location.state]);

    // Handle seat reservation
    const handleReservation = async () => {
        if (showEnded) {
            alert("This show has ended. Booking is no longer available.");
            return;
        }
        if (!user) {
            alert("You need to log in first!");
            navigate("/login");
            return;
        }

        if (selectedSeats.length === 0) {
            alert("Please select a seat.");
            return;
        }

        const confirmBooking = window.confirm("Are you sure, you want to continue?");
        if (!confirmBooking) {
            return;
        }

        const result = await reserveSeats(user.id, showId, selectedSeats);

        if (result.success) {
            // Keep session fresh after booking interaction
            if (typeof resetLogoutTimer === "function") {
                resetLogoutTimer();
            }
            // Persist booked seats into schedule entry under cinemaHall -> Hall -> schedule[] -> bookedSeats
            try {
                const mv = location.state?.movieSummary || {};
                const hallId = mv?.hall || "Hall1";
                const date = mv?.date || new Date().toISOString().slice(0, 10);
                const time = mv?.showtime || mv?.time || "20:00";
                const movieId = mv?.id;
                const selectedSeatObjs = seats.filter((s) => selectedSeats.includes(s.id));
                // Use readable labels; adjust if you prefer numeric IDs
                const seatLabels = selectedSeatObjs.map((s) => `${s.row_number}-${s.seat_number}`);
                await appendBookedSeatsToSchedule({ hallId, date, time, movieId, seats: seatLabels.length ? seatLabels : selectedSeats });
                // Also write a booking doc
                await saveBooking({
                    reservationId: result.reservationId,
                    userId: user.id,
                    userEmail: user.email,
                    movieId,
                    movieTitle: mv?.title || mv?.name || "",
                    hallId,
                    date,
                    time,
                    seats: seatLabels.length ? seatLabels : selectedSeats.map(String),
                    totalPrice,
                });
            } catch (e) {
                console.warn("Failed to write bookedSeats into schedule:", e);
            }
            navigate("/booking-confirmation", {
                state: {
                    reservationId: result.reservationId,
                    totalPrice: totalPrice,
                },
            });
        } else {
            alert(`Error reserving: ${result.message}`);
        }
    };


    // Helpers to normalize grid coordinates (support numbers, strings like "A", "Row 1", etc.)
    const toRowIndex = (v) => {
        if (v === null || v === undefined) return 0;
        const s = String(v).trim();
        const n = Number(s);
        if (!Number.isNaN(n)) return n <= 0 ? 1 : Math.floor(n);
        // Letter rows: A=1, B=2, ... Z=26
        const mAlpha = s.match(/^[A-Za-z]+$/);
        if (mAlpha) {
            const ch = s[0].toUpperCase();
            return Math.max(1, ch.charCodeAt(0) - 64);
        }
        // Extract first number in string
        const mNum = s.match(/(\d+)/);
        return mNum ? Math.max(1, Number(mNum[1])) : 0;
    };
    const toColIndex = (v) => {
        if (v === null || v === undefined) return 0;
        const s = String(v).trim();
        const n = Number(s);
        if (!Number.isNaN(n)) return n <= 0 ? 1 : Math.floor(n);
        const mNum = s.match(/(\d+)/);
        return mNum ? Math.max(1, Number(mNum[1])) : 0;
    };

    const normalizedSeats = seats.map((s) => ({
        ...s,
        rowIndex: toRowIndex(s.row_number),
        colIndex: toColIndex(s.seat_number),
    }));

    // Derive grid dimensions and helpers
    const maxRow = normalizedSeats.reduce((m, s) => Math.max(m, s.rowIndex || 0), 0);
    const maxCol = normalizedSeats.reduce((m, s) => Math.max(m, s.colIndex || 0), 0);

    // Ensure stable order using normalized indices
    const orderedSeats = [...normalizedSeats]
        .filter((s) => s.rowIndex > 0 && s.colIndex > 0)
        .sort((a, b) => (a.rowIndex - b.rowIndex) || (a.colIndex - b.colIndex));

    // Prepare sorted row labels for the aside label column
    const rowLabelMap = new Map();
    for (const s of orderedSeats) {
        if (!rowLabelMap.has(String(s.row_number))) {
            rowLabelMap.set(String(s.row_number), s.rowIndex);
        }
    }
    const rowLabels = Array.from(rowLabelMap.entries())
        .sort((a, b) => a[1] - b[1])
        .map(([label]) => label);

    // Build a fast lookup map for seat by row/col to fill the whole matrix
    const seatMap = new Map();
    for (const s of orderedSeats) {
        seatMap.set(`${s.rowIndex}-${s.colIndex}`, s);
    }
    const colLabels = Array.from({ length: Math.max(maxCol, 0) }, (_, i) => String(i + 1));

    // movie summary optionally passed from MoviePage
    const movie = location.state?.movieSummary || null;

    return (
        <main className="seat-page">
            <button className="back-button-2" onClick={() => navigate(-1)} aria-label="Go back">
                <IoArrowBackOutline />
            </button>

            <section className="seat-layout">
                {/* Left info panel */}
                <aside className="left-panel">
                    <figure className="poster-wrap">
                        {movie?.poster_path ? (
                            <img
                                className="poster"
                                src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                                alt={movie?.title || "Movie poster"}
                            />
                        ) : (
                            <figure className="poster placeholder" aria-label="No poster available">
                                <svg viewBox="0 0 200 300" width="100%" height="100%" aria-hidden="true">
                                    <rect x="0" y="0" width="200" height="300" fill="#333" />
                                    <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="#777">No image</text>
                                </svg>
                                <figcaption>Poster unavailable</figcaption>
                            </figure>
                        )}
                    </figure>
                    <header className="movie-meta">
                        <h3>{movie?.title || "Your selected show"}</h3>
                        {movie?.runtime ? (
                            <p>
                                <strong>Duration:</strong> <time dateTime={`PT${movie.runtime}M`}>{movie.runtime} minutes</time>
                            </p>
                        ) : null}
                        {movie?.genres?.length ? (
                            <p><strong>Genre:</strong> {movie.genres.map((g) => g.name).join(", ")}</p>
                        ) : null}
                        <p>
                            <strong>Show:</strong> {movie?.hall || "Hall"} {movie?.date ? `• ${movie.date}` : ""} • {" "}
                            <time>{movie?.showtime || "20:00"}</time>
                        </p>
                    </header>
                </aside>

                {/* Right main content */}
                <section className="right-main">

                    <figure className="screen-wrap" aria-labelledby="screen-caption">
                        <figcaption id="screen-caption" className="screen-text">SCREEN</figcaption>
                        <svg className="screen-svg" viewBox="0 0 1000 100" preserveAspectRatio="none" aria-hidden="true">
                            <path d="M20,80 Q500,10 980,80" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
                        </svg>
                    </figure>

                    <section className="seat-stage">
                        <ol className="row-numbers left" aria-hidden="true">
                            {rowLabels.length > 0 ? (
                                rowLabels.map((row) => (
                                    <li key={row} className="row-number">{row}</li>
                                ))
                            ) : null}
                        </ol>
                        <section
                            className="seat-grid"
                            style={{
                                gridTemplateColumns: `repeat(${Math.max(maxCol, 1)}, 1fr)`,
                                gridTemplateRows: `repeat(${Math.max(maxRow, 1)}, 1fr)`,
                            }}
                        >
                            {maxRow > 0 && maxCol > 0 ? (
                                Array.from({ length: maxRow * maxCol }, (_, idx) => {
                                    const r = Math.floor(idx / maxCol) + 1;
                                    const c = (idx % maxCol) + 1;
                                    const seat = seatMap.get(`${r}-${c}`);
                                    if (seat) {
                                        const isSelected = selectedSeats.includes(seat.id);
                                        const isBooked = !!seat.isbooked;
                                        return (
                                            <button
                                                key={`s-${seat.id}`}
                                                className={`seat ${isBooked ? "seat-booked" : "seat-free"} ${isSelected ? "seat-selected" : ""}`}
                                                style={{ gridRow: r, gridColumn: c }}
                                                disabled={isBooked && !isAdmin}
                                                aria-disabled={isBooked && !isAdmin}
                                                onClick={() => {
                                                    if (isBooked && isAdmin) {
                                                        deleteReservation(seat.id);
                                                    } else if (!isBooked) {
                                                        toggleSeatSelection(seat.id);
                                                    }
                                                }}
                                                aria-label={`Row ${seat.row_number} Seat ${seat.seat_number}`}
                                            >
                                                <MdEventSeat className="seat-icon" />
                                                <em className="seat-tooltip" role="tooltip">Row {seat.row_number} - Seat {seat.seat_number}</em>
                                            </button>
                                        );
                                    }
                                    // Placeholder ghost seat to complete the visual grid
                                    return (
                                        <article key={`g-${r}-${c}`} className="seat seat-ghost" style={{ gridRow: r, gridColumn: c }} aria-hidden="true">
                                            <MdEventSeat className="seat-icon" />
                                        </article>
                                    );
                                })
                            ) : (
                                <p>Loading seats...</p>
                            )}
                        </section>
                        <ol className="row-numbers right" aria-hidden="true">
                            {rowLabels.length > 0 ? (
                                rowLabels.map((row) => (
                                    <li key={row} className="row-number">{row}</li>
                                ))
                            ) : null}
                        </ol>
                    </section>

                    {colLabels.length > 0 && (
                        <ol
                            className="col-numbers"
                            style={{ gridTemplateColumns: `repeat(${Math.max(maxCol, 1)}, 1fr)` }}
                            aria-hidden="true"
                        >
                            {colLabels.map((c) => (
                                <li key={c} className="col-number">{c}</li>
                            ))}
                        </ol>
                    )}

                    <ul className="legend">
                        <li>
                            <svg className="legend-dot selected" width="10" height="10" aria-hidden="true"><circle cx="5" cy="5" r="5" /></svg>
                            Selected
                        </li>
                        <li>
                            <svg className="legend-dot available" width="10" height="10" aria-hidden="true"><circle cx="5" cy="5" r="5" /></svg>
                            Available
                        </li>
                        <li>
                            <svg className="legend-dot taken" width="10" height="10" aria-hidden="true"><circle cx="5" cy="5" r="5" /></svg>
                            Taken
                        </li>
                    </ul>

                    <section className="summary" aria-live="polite">
                        {selectedSeats.length > 0 && (
                            <>
                                <p>Selected Seats: {selectedSeats.join(", ")}</p>
                                <p><strong>Total Price: €{totalPrice.toFixed(2)}</strong></p>
                            </>
                        )}
                    </section>
                </section>
            </section>

            <button
                className={`booking-fab ${selectedSeats.length > 0 && !showEnded ? "active" : ""}`}
                disabled={selectedSeats.length === 0 || showEnded}
                onClick={handleReservation}
                aria-label={showEnded ? "Show ended" : "Continue with booking"}
                title={showEnded ? "This show has ended" : "Continue with booking"}
            >
                <IoArrowForward />
            </button>

            {isAdmin && (
                <button className="admin-button" onClick={() => setShowChart(!showChart)}>
                    {showChart ? "Hide Chart" : "Show Chart"}
                </button>
            )}
            {isAdmin && showChart && stats && stats.available_seats !== undefined && stats.booked_seats !== undefined && (
                <section className="chart-container">
                    <article className="chart-text">
                        <h3>Seat Availability</h3>
                        <p>Available Seats: {stats.available_seats}</p>
                        <p>Booked Seats: {stats.booked_seats}</p>
                        <p>Total Revenue: €{stats.revenue.toFixed(2)}</p>
                    </article>
                    <figure className="chart-graph">
                        <Pie
                            data={{
                                labels: ["Available Seats", "Booked Seats"],
                                datasets: [{
                                    data: [stats.available_seats, stats.booked_seats],
                                    backgroundColor: ["#4CAF50", "#E57373"],
                                }],
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        labels: {
                                            color: "white",
                                            font: { size: 14 },
                                        },
                                    },
                                    tooltip: {
                                        bodyFont: { size: 14 },
                                        titleFont: { size: 16 },
                                        titleColor: "white",
                                        bodyColor: "white",
                                    },
                                },
                            }}
                        />
                        <figcaption>Distribution of available vs. booked seats</figcaption>
                    </figure>
                </section>
            )}
        </main>
    );
}
